import { createRouter, createWebHistory } from "vue-router";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import WorkflowLayout from "../layouts/WorkflowLayout.vue";
import AlgorithmTraceLogView from "../views/AlgorithmTraceLogView.vue";
import BulkOrderCreationView from "../views/BulkOrderCreationView.vue";
import CalcView from "../views/CalcView.vue";
import CalculateShippingPosView from "../views/CalculateShippingPosView.vue";
import DashboardOverviewView from "../views/DashboardOverviewView.vue";
import HomeView from "../views/HomeView.vue";
import IntermediateOrderSkuDistributionView from "../views/IntermediateOrderSkuDistributionView.vue";
import InventoryHealthView from "../views/InventoryHealthView.vue";
import ModulePlaceholderView from "../views/ModulePlaceholderView.vue";
import ReportsView from "../views/ReportsView.vue";
import ResultsView from "../views/ResultsView.vue";
import SkuAnalysisView from "../views/SkuAnalysisView.vue";
import TransfersView from "../views/TransfersView.vue";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      component: DashboardLayout,
      children: [
        {
          path: "",
          name: "dashboard-overview",
          component: DashboardOverviewView,
          meta: {
            dashboardNavKey: "dashboard-overview",
            helpTopicId: "insight-iq",
            pageTitle: "Insight.IQ",
            pageSubtitle:
              "Redistribution impact across supply chain and e-business metrics",
          },
        },
        {
          path: "sku-analysis",
          name: "dashboard-sku-analysis",
          component: SkuAnalysisView,
          meta: {
            dashboardNavKey: "dashboard-sku-analysis",
            helpTopicId: "sku-analysis",
            pageTitle: "SKU Analysis",
            pageSubtitle: "All SKUs assessed during redistribution",
          },
        },
        {
          path: "inventory-health",
          name: "dashboard-inventory-health",
          component: InventoryHealthView,
          meta: {
            dashboardNavKey: "dashboard-inventory-health",
            helpTopicId: "inventory-health",
            pageTitle: "Inventory Health",
            pageSubtitle:
              "Before-and-after PO health impact from redistribution",
          },
        },
        {
          path: "transfers",
          name: "dashboard-transfers",
          component: TransfersView,
          meta: {
            dashboardNavKey: "dashboard-transfers",
            helpTopicId: "transfers",
            pageTitle: "Transfers",
            pageSubtitle: "Review movement decisions and transfer readiness",
          },
        },
        {
          path: "reports",
          name: "dashboard-reports",
          component: ReportsView,
          meta: {
            dashboardNavKey: "dashboard-reports",
            helpTopicId: "reports",
            pageTitle: "Reports",
            pageSubtitle:
              "Operational summaries from the latest redistribution run",
          },
        },
        {
          path: "algorithm-trace-log",
          name: "dashboard-algorithm-trace-log",
          component: AlgorithmTraceLogView,
          meta: {
            dashboardNavKey: "dashboard-algorithm-trace-log",
            helpTopicId: "algorithm-trace-log",
            pageTitle: "Algorithm Trace Log",
            pageSubtitle: "Run-level diagnostics and algorithm decisions",
          },
        },
        {
          path: "settings",
          name: "dashboard-settings",
          component: ModulePlaceholderView,
          meta: {
            dashboardNavKey: "dashboard-settings",
            helpTopicId: "settings",
            pageTitle: "Settings",
            pageSubtitle:
              "Manage dashboard defaults and control-tower preferences",
            pageDescription:
              "Settings will be added after the landing page MVP stabilizes and defaults are finalized.",
          },
        },
        {
          path: "",
          component: WorkflowLayout,
          children: [
            {
              path: "po-recommendations",
              name: "search-select-pos",
              component: HomeView,
              meta: {
                dashboardNavKey: "search-select-pos",
                helpTopicId: "po-recommendations",
                pageTitle: "PO Recommendations",
                pageSubtitle:
                  "Optimize purchase order allocation to prevent stockouts",
              },
            },
            {
              path: "results",
              name: "results",
              component: ResultsView,
              meta: {
                dashboardNavKey: "results",
                helpTopicId: "redistribution-results",
                pageTitle: "Redistribution Results",
                pageSubtitle:
                  "Review the outcome of the latest redistribution run",
              },
            },
            {
              path: "calc",
              name: "calc",
              component: CalcView,
              meta: {
                dashboardNavKey: "search-select-pos",
                helpTopicId: "redistribution-calculation",
                pageTitle: "Redistribution Calculation",
                pageSubtitle:
                  "Optimize purchase order allocation to prevent stockouts",
              },
            },
            {
              path: "bulk-order-creation",
              name: "dashboard-bulk-order-creation",
              component: BulkOrderCreationView,
              meta: {
                dashboardNavKey: "dashboard-bulk-order-creation",
                helpTopicId: "bulk-order-creation",
                pageTitle: "Shipping PO Creation",
                pageSubtitle:
                  "Find a Bulk Order or Intermediate Order and review its SKU lines",
              },
            },
            {
              path: "bulk-order-creation/calculate",
              name: "dashboard-bulk-order-creation-calculate",
              component: CalculateShippingPosView,
              meta: {
                dashboardNavKey: "dashboard-bulk-order-creation",
                helpTopicId: "bulk-order-creation",
                pageTitle: "Calculate Shipping POs",
                pageSubtitle:
                  "Generate suggested Shipping POs from the reviewed order contents",
              },
            },
            {
              path: "intermediate-order-sku-distribution",
              name: "dashboard-intermediate-order-sku-distribution",
              component: IntermediateOrderSkuDistributionView,
              meta: {
                dashboardNavKey:
                  "dashboard-intermediate-order-sku-distribution",
                helpTopicId: "intermediate-order-sku-distribution",
                pageTitle: "Intermediate Order SKU Distribution",
                pageSubtitle:
                  "Track how SKUs are distributed across Intermediate Orders and warehouses",
              },
            },
          ],
        },
      ],
    },
  ],
});

// Preserve sticky query parameters (e2e test flag, demo mode) across navigation
const STICKY_QUERY_PARAMS = ["test", "demo"];
router.beforeEach((to, from) => {
  const missing = STICKY_QUERY_PARAMS.filter(
    (param) => from.query[param] && !to.query[param]
  );
  if (missing.length === 0) return;
  const query = { ...to.query };
  for (const param of missing) {
    const value = from.query[param];
    if (value) query[param] = value;
  }
  return { ...to, query };
});

export default router;
